/**
 * Omniversail Language Center — app entry point.
 *
 * Order matters: `globals.css` must be imported BEFORE anything that references
 * the design tokens (--background, --brand, ...). The upstream monolith had this
 * import commented out and relied on other stylesheets happening to load first;
 * that is why some widgets rendered unstyled. We load it explicitly here.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';

import './styles/globals.css';
import './styles/animations.css';
import './styles/App.css';
import './styles/app-shell.css';
// Loaded eagerly (not from the lazy LandingPage chunk) so the home-page hero is
// styled on first paint — a code-split landing.css arrived after React mounted on
// cold loads, producing a flash of unstyled (horizontal) content until refresh.
import './styles/landing.css';

import './i18n/config';
import App from './App';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found in index.html');
}

/*
 * Self-heal against a stale index.html.
 *
 * The deployed HTML is a single-page app whose chunks are content-hashed and
 * deleted when a new release is promoted. If a browser (or an edge cache) is
 * holding an older index.html, the chunk request 404s, React unmounts and the
 * visitor gets a blank page. Nothing retries, so the failure is permanent for
 * that visitor until they hard-refresh.
 *
 * Two triggers, one reload per session:
 *   1. a chunk-load error (window error / unhandled rejection), and
 *   2. the app never painted (root still empty after a grace period), which
 *      catches the case where the failed chunk is the landing page itself.
 *
 * The sessionStorage guard stops a reload loop if the edge is still serving
 * the stale document.
 */
const STALE_RELOAD_KEY = 'omniversail:stale-reload';
const CHUNK_ERROR_RE =
  /Loading chunk \d+ failed|Loading CSS chunk|Importing a module script failed|error loading dynamically imported module/i;

function reloadOnce() {
  try {
    if (window.sessionStorage.getItem(STALE_RELOAD_KEY)) return;
    window.sessionStorage.setItem(STALE_RELOAD_KEY, '1');
  } catch (e) {
    // Private mode / storage disabled: still allow a single reload attempt.
  }
  window.location.reload();
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const message = (event && (event.message || (event.error && event.error.message))) || '';
    if (CHUNK_ERROR_RE.test(message)) reloadOnce();
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;
    const message = (reason && (reason.message || reason)) || '';
    if (CHUNK_ERROR_RE.test(String(message))) reloadOnce();
  });
  window.setTimeout(() => {
    if (container && container.childElementCount === 0) reloadOnce();
  }, 8000);
}

ReactDOM.createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
